import { FC, useState } from 'react';

import { AvailableCurrencies } from 'utils/clickme';
import { CampaignProductType } from 'utils/constants/campaign';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import { format, formatCost, pluralize } from '@hh.ru/front-static-app';
import {
    ActionBar,
    BottomSheet,
    Button,
    ChipsContainer,
    CustomChip,
    Modal,
    NumberInput,
    Text,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import FormatMoney from 'components/FormatMoney';

import { batchIncreaseBudget } from 'components/common/BudgetIncreaseModal/batchIncreaseBudget';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { setCampaignIdsForBudgetIncrease, setIsBudgetIncreaseModalVisible } from 'store/models/campaignsDashboard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'BudgetIncreaseModal.title',
    titleForManyCampaigns: 'BudgetIncreaseModal.titleForManyCampaigns',
    titleDescription: 'BudgetIncreaseModal.titleDescription',
    titleDescriptionForManyCampaigns: 'BudgetIncreaseModal.titleDescriptionForManyCampaigns',
    placeholder: 'BudgetIncreaseModal.input.placeholder',
    total: 'BudgetIncreaseModal.total',
    balance: 'BudgetIncreaseModal.balance',
    confirmButton: 'BudgetIncreaseModal.button.confirm',
    cancelButton: 'BudgetIncreaseModal.button.cancel',
    currency: 'currency.short',
    error: {
        minBudgetValue: 'BudgetIncreaseModal.error.minBudgetValue',
        maxBudgetValue: 'BudgetIncreaseModal.error.maxBudgetValue',
    },
    campaigns: {
        zero: 'BudgetIncreaseModal.campaigns.many',
        one: 'BudgetIncreaseModal.campaigns.one',
        some: 'BudgetIncreaseModal.campaigns.many',
        many: 'BudgetIncreaseModal.campaigns.many',
    },
};

const MIN_BUDGET = 500;
const MAX_BUDGET = 100000;
const DEFAULT_AMOUNT = 10000;
const SUGGESTED_AMOUNTS = [15000, 25000, 50000];

const useGetTitleDescription = (): string => {
    const campaignIdsForBudgetIncrease = useSelector((state) => state.campaignsDashboard.campaignIdsForBudgetIncrease);
    const campaigns = useSelector((state) => state.campaignsDashboard.campaigns);

    const trls = useTrls();

    if (campaignIdsForBudgetIncrease.size === 1) {
        const campaignIds = [...campaignIdsForBudgetIncrease];
        const campaign = campaigns.get(Number(campaignIds[0]));
        if (!campaign) return '';
        const { vacancyName, productType, name: campaignName } = campaign;
        const campaignNameForDescription =
            productType === CampaignProductType.SearchAuction ? vacancyName : campaignName;

        return trls(TrlKeys.titleDescription, [campaignNameForDescription]);
    }

    return trls(TrlKeys.titleDescriptionForManyCampaigns);
};

export const BudgetIncreaseModal: FC = () => {
    const campaignIdsForBudgetIncrease = useSelector((state) => state.campaignsDashboard.campaignIdsForBudgetIncrease);
    const realBalance = useSelector((state) => state.balance?.realBalance);

    const isBudgetIncreaseModalVisible = useSelector((state) => state.campaignsDashboard.isBudgetIncreaseModalVisible);
    const [budgetIncreaseAmount, setBudgetIncreaseAmount] = useState(String(DEFAULT_AMOUNT));
    const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);
    const [validationError, setValidationError] = useState('');

    const trls = useTrls();
    const dispatch = useDispatch();
    const { abortRequest, abortSignal } = useRequestCancel();

    const title =
        campaignIdsForBudgetIncrease.size === 1
            ? trls(TrlKeys.title)
            : trls(TrlKeys.titleForManyCampaigns, [
                  pluralize(campaignIdsForBudgetIncrease.size, {
                      zero: trls(TrlKeys.campaigns.zero),
                      one: trls(TrlKeys.campaigns.one),
                      some: trls(TrlKeys.campaigns.many),
                      many: trls(TrlKeys.campaigns.many),
                  }),
              ]);
    const titleDescription = useGetTitleDescription();

    const onCloseHandler = () => dispatch(setIsBudgetIncreaseModalVisible(false));
    const onAfterExitHandler = () => {
        if (!isBudgetIncreaseModalVisible) {
            dispatch(setCampaignIdsForBudgetIncrease(new Set()));
        }
    };

    const validateBudget = (value: string): boolean => {
        if (!value || Number(value) < MIN_BUDGET) {
            setValidationError(trls(TrlKeys.error.minBudgetValue, [MIN_BUDGET]));
            return false;
        }
        if (Number(value) > MAX_BUDGET) {
            setValidationError(trls(TrlKeys.error.maxBudgetValue, [MAX_BUDGET]));
            return false;
        }
        setValidationError('');
        return true;
    };

    const onChangeHandler = (value: string) => {
        validateBudget(value);
        setBudgetIncreaseAmount(value);
    };

    const onConfirm = () => {
        const isValid = validateBudget(budgetIncreaseAmount);
        if (!isValid) return;

        abortRequest();
        setIsLoadingOn();

        dispatch(batchIncreaseBudget(budgetIncreaseAmount, abortSignal())).finally(() => {
            setIsLoadingOff();
            onCloseHandler();
        });
    };

    const content = (
        <>
            <VSpacingContainer default={16}>
                <NumberInput
                    id="budget-increase-input"
                    decimalMark={[]}
                    decimalLength={0}
                    onChange={onChangeHandler}
                    placeholder={trls(TrlKeys.placeholder)}
                    postfix={trls(TrlKeys.currency)}
                    size="medium"
                    value={budgetIncreaseAmount}
                    invalid={!!validationError}
                    errorMessage={validationError}
                />
                <ChipsContainer>
                    {SUGGESTED_AMOUNTS.map((amount, index) => (
                        <CustomChip
                            key={index}
                            Element="button"
                            size="small"
                            onClick={() => {
                                setBudgetIncreaseAmount(String(amount));
                            }}
                        >
                            {formatCost(amount, NON_BREAKING_SPACE)}
                            {NON_BREAKING_SPACE}
                            {trls(TrlKeys.currency)}
                        </CustomChip>
                    ))}
                </ChipsContainer>
            </VSpacingContainer>
            <VSpacing default={24} />
        </>
    );

    const confirmButton = (
        <Button
            data-qa="increase-budget-confirm-button"
            mode="primary"
            style="accent"
            onClick={onConfirm}
            size="medium"
            key="increase-budget-confirm"
            loading={isLoading}
        >
            {trls(TrlKeys.confirmButton)}
        </Button>
    );

    const cancelButton = (
        <Button
            data-qa="increase-budget-cancel-button"
            mode="secondary"
            style="accent"
            onClick={onCloseHandler}
            size="medium"
        >
            {trls(TrlKeys.cancelButton)}
        </Button>
    );

    const additionalContent = campaignIdsForBudgetIncrease.size > 1 && (
        <VSpacingContainer default={4}>
            <Text typography="label-2-regular" style="primary">
                {format(trls(TrlKeys.total), {
                    '[0]': (
                        <FormatMoney groupSeparator={NON_BREAKING_SPACE} currency={AvailableCurrencies.RUR}>
                            {Number(budgetIncreaseAmount) * campaignIdsForBudgetIncrease.size}
                        </FormatMoney>
                    ),
                })}
            </Text>
            <Text typography="label-2-regular" style="secondary">
                {format(trls(TrlKeys.balance), {
                    '[0]': (
                        <FormatMoney groupSeparator={NON_BREAKING_SPACE} currency={AvailableCurrencies.RUR}>
                            {realBalance}
                        </FormatMoney>
                    ),
                })}
            </Text>
        </VSpacingContainer>
    );

    const desktopFooter = (
        <ActionBar
            showDivider={campaignIdsForBudgetIncrease.size > 1 ? 'always' : undefined}
            type={campaignIdsForBudgetIncrease.size === 1 ? 'vertical' : 'horizontal'}
            additionalContent={additionalContent}
            primaryActions={confirmButton}
        />
    );

    const mobileFooter = (
        <ActionBar
            showDivider="always"
            type="vertical"
            additionalContent={additionalContent}
            primaryActions={confirmButton}
            secondaryActions={cancelButton}
        />
    );

    return (
        <>
            <Modal
                size="small"
                visible={isBudgetIncreaseModalVisible}
                onClose={onCloseHandler}
                onAfterExit={onAfterExitHandler}
                header={
                    <MagritteModalHeader
                        title={title}
                        subtitle={
                            <Text typography="paragraph-2-regular" style="secondary">
                                {titleDescription}
                            </Text>
                        }
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onCloseHandler} />}
                    />
                }
                footer={desktopFooter}
                data-qa="budget-increase-modal"
            >
                {content}
            </Modal>
            <BottomSheet
                height="content"
                visible={isBudgetIncreaseModalVisible}
                onClose={onCloseHandler}
                onAfterExit={onAfterExitHandler}
                footer={mobileFooter}
                data-qa="budget-increase-bottom-sheet"
            >
                <VSpacingContainer default={8}>
                    <Text typography="title-4-semibold">{title}</Text>
                    {titleDescription && (
                        <Text typography="paragraph-2-regular" style="secondary">
                            {titleDescription}
                        </Text>
                    )}
                </VSpacingContainer>
                <VSpacing default={16} />
                {content}
            </BottomSheet>
        </>
    );
};
